import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScrollToTop from '../../../components/ScrollToTop';

const STANDARDS = [
    {
        title: 'Respect & Consent',
        intro: 'Every interaction on TLK must be consensual and respectful. This means:',
        points: [
            'Treat creators, their digital selves, and other users with respect',
            'Do not send unwanted, harassing, or abusive messages',
            'Respect boundaries set by creators and the platform',
        ],
    },
    {
        title: 'Authentic Use Only',
        intro: 'TLK is designed for genuine fan-to-creator connection. You may not:',
        points: [
            'Impersonate another person, creator, or entity',
            'Use automated systems, bots, or scripts to interact with the service',
        ],
    },
];

const PROHIBITED = [
    'Hate speech, threats, or harassment of any kind',
    'Content that promotes violence or illegal activity',
    'Sexually explicit content, nudity, or solicitation outside boundaries',
    'Spam, scams, phishing, or fraudulent behavior',
];

const ENFORCEMENT_ACTIONS = [
    'Warnings or temporary restrictions',
    'Permanent suspension of your account',
    'Reporting to law enforcement where required by law',
];

const CommunityStandards = () => {
    const navigate = useNavigate();

    return (
        <>
            <Helmet>
                <title>Community Standards</title>
            </Helmet>
            <ScrollToTop />
            <div className="community-standards-page">
                <header className="cs-header">
                    <div className="cs-header-left">
                        <button type="button" className="cs-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
                            <ArrowBackIcon fontSize="small" />
                        </button>
                        <div className="cs-title">
                            <span className="cs-bar"></span>
                            <h1>Community Standards</h1>
                        </div>
                    </div>
                    <div className="cs-updated">Last updated: August 2026</div>
                </header>

                <main className="cs-main">
                    <div className="cs-content">

                        <section className="cs-card cs-intro-card">
                            <h2>Our Commitment</h2>
                            <p>
                                TLK is built on mutual respect. We want every user and creator to feel safe, heard, and in
                                control. Users who violate these standards will be removed from the platform.
                            </p>
                        </section>

                        {STANDARDS.map((standard, index) => (
                            <section className="cs-card" key={standard.title}>
                                <h2>
                                    <span className="cs-num">{index + 1}.</span> {standard.title}
                                </h2>
                                <p className="cs-lead">{standard.intro}</p>
                                <ul className="cs-list">
                                    {standard.points.map((point) => (
                                        <li key={point}>
                                            <span className="cs-dot"></span>
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}

                        <section className="cs-card">
                            <h2>
                                <span className="cs-num">3.</span> Prohibited Content &amp; Conduct
                            </h2>
                            <p className="cs-lead">The following is strictly prohibited on TLK:</p>
                            <div className="cs-grid">
                                {PROHIBITED.map((item) => (
                                    <div className="cs-grid-item" key={item}>{item}</div>
                                ))}
                            </div>
                        </section>

                        <section className="cs-card">
                            <h2>
                                <span className="cs-num">4.</span> Enforcement
                            </h2>
                            <div className="cs-enforcement-box">
                                <p className="cs-enforcement-title">Violations have consequences.</p>
                                <p className="cs-lead cs-small">
                                    Depending on the severity and context of a violation, we may take action including:
                                </p>
                                <ul className="cs-plain-list">
                                    {ENFORCEMENT_ACTIONS.map((action) => (
                                        <li key={action}>• {action}</li>
                                    ))}
                                </ul>
                            </div>
                        </section>
                        <section className="cs-card">
                            <h2 className="text-lg font-semibold mb-3"> 
                                <span className="cs-num">5.</span>Prohibited AI-Generated Content 
                            </h2>
                            <p className="mb-2">
                                The creation, upload, distribution, or use of deep fake content, non-consensual
                                impersonation, and any illegal or policy-violating AI-generated content is strictly
                                prohibited on TLK.
                            </p>
                            <p>
                                To report content, see our{' '}
                                <Link className="underline" to="/complaints-policy">
                                    Complaints &amp; Content Removal Policy
                                </Link>
                                .
                            </p>
                        </section>
                        <section className="cs-report">
                            <h3>Seen something that violates these rules?</h3>
                            <button type="button" className="cs-report-btn" onClick={() => navigate('/user/help')}>
                                Report a Violation
                            </button>
                        </section>

                    </div>
                </main>
            </div>
        </>
    );
};

export default CommunityStandards;